import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="25843" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-24 bg-muted/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group inline-flex h-9 w-max items-center justify-center rounded-lg bg-transparent px-2 py-2 text-sm font-medium transition-colors hover:text-secondary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50", "font-sans uppercase tracking-widest text-foreground hover:bg-transparent")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-full bg-muted/10 animate-pulse rounded" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium uppercase tracking-widest text-foreground hover:text-secondary transition-colors border-b border-border/10 pb-2" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4 pt-2 flex flex-col gap-3">
        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5 uppercase tracking-widest font-bold rounded-lg" size="lg" asChild>
          <Link to="tel:5155859542">
            <Phone className="mr-2 h-4 w-4" />
            Call Now
          </Link>
        </Button>
        <Button className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 uppercase tracking-widest font-bold rounded-lg shadow-md" size="lg" asChild>
          <Link to="/ContactPage">Request Free Inspection</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border/10 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50 relative">
          <span className="text-xl font-bold tracking-tighter text-foreground">Crawlspace Professionals in Des Moines</span>
        </Link>

        {}
        <div className="hidden md:flex items-center gap-4">
          <MenuProvider menu_id="44">
            <DesktopMenuConsumer />
          </MenuProvider>
          
          <div className="flex items-center gap-3 ml-4">
            <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/5 font-sans uppercase tracking-widest text-xs font-bold px-4 h-10 rounded-lg transition-transform hover:-translate-y-0.5 active:translate-y-0" asChild>
              <Link to="tel:5155859542">
                <Phone className="mr-2 h-3 w-3" />
                Call Now
              </Link>
            </Button>
            <Button variant="default" size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-sans uppercase tracking-widest text-xs font-bold px-6 h-10 rounded-lg shadow-md transition-transform hover:-translate-y-0.5 active:translate-y-0" asChild>
              <Link to="/ContactPage">Request Free Inspection</Link>
            </Button>
          </div>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-secondary hover:bg-transparent z-50" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label={isMobileOpen ? "Close menu" : "Open menu"}>
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        {}
        <div className={cn("fixed inset-0 bg-background z-40 md:hidden transition-transform duration-300 ease-in-out pt-24 px-4 h-screen w-screen overflow-y-auto", isMobileOpen ? "translate-x-0" : "translate-x-full")}>
          <MenuProvider menu_id="44">
            <MobileMenuConsumer onClose={() => setIsMobileOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}